import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Trash2, X, Loader2, Maximize2 } from 'lucide-react';
import api from "../../../../api/axios";

const Sliders = () => {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL;

  const fetchSliders = async () => {
    try {
      const res = await api.get("/sliders");
      if (res.data.success) {
        setSliders(res.data.data);
      }
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { fetchSliders(); }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    
    // Client-side validation to prevent unnecessary 500 errors
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert("Format non supporté. Utilisez WebP, JPG ou PNG.");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('image', file); // 'image' matches the Controller $request->file('image')

    try {
      const res = await api.post("/sliders", formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        fetchSliders();
      }
    } catch (err) {
      console.error("Upload error:", err.response?.data);
      alert(err.response?.data?.message || "Erreur lors de l'upload.");
    } finally { setUploading(false); }
  };

  // --- DRAG AND DROP HANDLERS ---
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files[0]);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce slider ?")) return;
    try {
      await api.delete(`/sliders/${id}`);
      setSliders(sliders.filter(s => s.id !== id));
    } catch (err) { alert("Erreur suppression"); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-10 text-center md:text-left">
        <h2 className="text-3xl font-black uppercase tracking-tighter text-gray-900">Gestion des Sliders</h2>
        <p className="text-sm font-bold text-[#8A8A89]">Ajoutez vos bannières publicitaires (Format recommandé: 1920x800)</p>
      </div>

      <div style={{display:"none"}}>{loading}</div>

      {/* DRAG & DROP ZONE */}
      <div className="mb-12">
        <div 
          onDragOver={onDragOver}
          onDrop={onDrop}
          className="relative group cursor-pointer border-4 border-dashed border-gray-100 rounded-[3rem] p-12 flex flex-col items-center justify-center transition-all hover:border-[#ffaa00] hover:bg-orange-50/30"
        >
          <input 
            type="file" 
            id="slider-upload"
            className="hidden" 
            onChange={(e) => handleUpload(e.target.files[0])}
            accept="image/*"
          />
          <label htmlFor="slider-upload" className="flex flex-col items-center cursor-pointer">
            {uploading ? (
              <Loader2 className="animate-spin text-[#ffaa00] mb-4" size={48} />
            ) : (
              <div className="bg-gray-50 p-6 rounded-full text-[#ffaa00] mb-4 group-hover:scale-110 transition-transform">
                <Upload size={40} />
              </div>
            )}
            <h3 className="text-xl font-black text-gray-900 uppercase">Déposer l'image ici</h3>
            <p className="text-xs font-bold text-gray-400 mt-2 uppercase tracking-widest text-center">
              WebP, PNG ou JPG (Max 5MB)
            </p>
          </label>
        </div>
      </div>

      {/* TABLE VIEW */}
      <div className="bg-white rounded-[2.5rem] shadow-xl border border-gray-100 overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-50/50 border-b border-gray-100 text-[10px] font-black uppercase text-[#8A8A89]">
              <th className="px-8 py-6">Visuel</th>
              <th className="px-8 py-6">Nom du fichier</th>
              <th className="px-8 py-6 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {sliders.map((slider) => (
              <tr key={slider.id} className="group hover:bg-gray-50/50">
                <td className="px-8 py-5">
                  <div 
                    onClick={() => setSelectedImage(slider.image)}
                    className="w-48 h-24 bg-gray-100 rounded-2xl overflow-hidden border border-gray-100 cursor-zoom-in relative group"
                  >
                    <img src={`${BACKEND_BASE}/storage/${slider.image}`} className="w-full h-full object-cover" alt="" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white"><Maximize2 size={20}/></div>
                  </div>
                </td>
                <td className="px-8 py-5 text-[10px] font-bold text-gray-400">{slider.image}</td>
                <td className="px-8 py-5 text-right">
                  <button onClick={() => handleDelete(slider.id)} className="p-3 bg-red-50 text-red-400 hover:bg-red-500 hover:text-white rounded-xl transition-all"><Trash2 size={18} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* LIGHTBOX POPUP */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelectedImage(null)} className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4">
            <X className="absolute top-8 right-8 text-white cursor-pointer" size={40} />
            <img src={`${BACKEND_BASE}/storage/${selectedImage}`} className="max-w-full max-h-[85vh] rounded-3xl" alt="" />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Sliders;